import {h} from 'preact';
import {useContext, useState, createContext, useEffect} from "preact/compat";
import {useCookies} from "../../hooks";
import {INITIAL_APPLICATION_ALERTS_STATE} from "./appContext.data";

// Creates a new empty state
const AppContext = createContext(null);

// This is the main app context in which we will store data such as session, location, login data, etc.
export const AppContextProvider = props => {
    // Destructured props
    const {children} = props;

    // State to store all the available store locations
    const [locations, setLocations] = useState([]);

    const [isLocationPickerOpen, setIsLocationPickerOpen] = useState(false);
    const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);

    // State to store all recaptcha component data
    const [recaptchaData, setRecaptchaData] = useState({
        isEnabled: null,
        token:     ""
    });

    // State to store all notices data
    const [noticesData, setNoticesData] = useState([]);

    // State for the social login feature config data.
    // Everything is false by default to prevent component flicking.
    const [socialLoginConfig, setSocialLoginConfig] = useState({
        isGeneralEnabled:        false,
        isAccountLinkingEnabled: false,
        isGoogleEnabled:         false,
        isFacebookEnabled:       false
    })
    // State to store some general application data.
    // Data here comes mostly from the "3 columns" template renderer,
    // but it isn't restricted to it.
    const [generalApplicationData, setGeneralApplicationData] = useState({});
    // State to control the full page loader
    // In this case, we can directly change its value by the state setter as it isn't a critical data state
    const [fullPageLoader, setFullPageLoader] = useState(false);
    // The same rule stands for this state
    const [fullPageLoaderV2, setFullPageLoaderV2] = useState(false);

    // Cookie to store the current location that data will be flowed from
    const [storeLocation, setStoreLocation] = useCookies("STORE_LOCATION", {
        id:   1,
        name: "Brasília - DF" // This will be the default value for the application
    });
    // Cookie to control accessibility font size
    const [accessibilityFont, setAccessibilityFont] = useCookies("ACCESSIBILITY_FONT", 0);
    // Cookie to store the dark mode theme state
    const [highContrast, setHighContrast] = useCookies("HIGH_CONTRAST", false);
    // Cookie to store handTalk data
    const [handTalkData, setHandTalkData] = useCookies(["HAND_TALK"], {
        enable: null,
        token:  ""
    });
    // All application alerts.
    // It's meant to be as simple as possible, that's why we only have two properties in this object.
    // Both the alerts (success and error) are controlled by whether a value exists in any of these properties and,
    // if not, just hide it.
    const [applicationAlerts, setApplicationAlerts] = useState(INITIAL_APPLICATION_ALERTS_STATE);

    // TODO: this is deprecated.
    //  Remove it later.
    // State to store develop mode status for Preact server
    const [developMode] = useCookies("DEVELOP_MODE", false);

    // Returns the formatted backend url without a / in the end
    let SERVER_URL =
        process.env.NODE_ENV === "development" ?
            `${process.env.PREACT_APP_BACKEND_URL}` :
            "";

    // Checks if the last character is a / and, if so, removes it
    SERVER_URL = !SERVER_URL.endsWith("/") ? "/media/" : "media/";

    // Handles the font size accessibility feature
    const handleAccessibilityFont = (fontSize) => {
        if (fontSize > 2 || fontSize < 0) {
            return;
        }

        setAccessibilityFont(fontSize);
    }

    // Handles the high contrast feature
    const handleHighContrast = () => {
        setHighContrast(!highContrast);
    }

    // Method to handle all the needed HandTalk feature data
    const handleHandTalkData = () => {
        // Tries to retrieve the HandTalk data from the 3columns renderer script data
        const scriptTag = document.getElementById(`handtalk_data`);

        // Exits the function in case a script tag doesn't exist
        if (!scriptTag) {
            console.warn('HandTalk data script not found!');

            return;
        }

        // Parses the given data
        const handTalkData = JSON.parse(scriptTag.textContent);

        // Finally, sets the parsed data to the browser cookie
        setHandTalkData(handTalkData);
    }

    // Method to gather all the basic application data from the 3columns renderer script tag
    const handleGeneralApplicationData = () => {
        try {
            // Looks for the script itself inside the document
            let scriptData = document.querySelector("#general_application_data");

            // If one doesn't exist, then it warns the user and exits the function
            if (!scriptData) {
                console.warn("Application data script tag not found!");

                return;
            }

            // Otherwise, it parses its content
            const parsedData = JSON.parse(scriptData.textContent);

            // And, finally, sets it to the generalApplicationData state
            setGeneralApplicationData(parsedData);
        } catch (e) {
            console.error(`Uncaught error:`, e);
        }
    }

    // Method to update the document title.
    // If no argument is passed to the function,
    // then it uses only the default value.
    const handleDocumentTitle = (
        title = ""
    ) => {
        // Additional validation layer
        const dataBind =
            generalApplicationData["defaultStoreTitle"] ||
            "Loja Virtual | Adquira do conforto da sua casa";

        try {
            // Actually tries to set the document title with the given data
            document.title = title ?
                `${title} | ${dataBind}` :
                dataBind;
        } catch (err) {
            // In case of any error, then it warns it into the console
            console.warn("Uncaught error while trying to update document title!");

            // And then sets the default value
            document.title = "Loja Virtual | Adquira do conforto da sua casa";
        }
    }

    // Procedure to handle all application alerts
    const handleApplicationAlerts = (props) => {
        // Destructured props
        const {
            type = undefined,
            content = undefined,
            // Special flag to clear all alerts at once
            reset = false
        } = props;

        // Resets all alerts at once
        if (reset) {
            setApplicationAlerts(INITIAL_APPLICATION_ALERTS_STATE);

            return;
        }

        // Error prevention block
        if (!type || !content) {
            return;
        }

        setApplicationAlerts(prevState => {
            return {
                ...prevState,
                [type]: content
            }
        });
    }

    // Effect to handle the contrast accessibility feature
    useEffect(() => {
        const htmlElement = document.documentElement;

        if (highContrast) {
            htmlElement.classList.add('dark');
            htmlElement.classList.add('high-contrast');
        } else {
            htmlElement.classList.remove('dark');
            htmlElement.classList.remove('high-contrast');
        }
    }, [highContrast]);

    // Effect to handle the initial application setup flow
    useEffect(() => {
        handleHandTalkData();
        handleGeneralApplicationData();

        // TODO: Deprecated. Remove later...
        if (developMode) {
            console.log('modo de desenvolvimento ativado.');
        }
    }, []);

    const PATH_LOCATION = window.location.pathname;

    // Effect to clear the application alerts at each path change
    useEffect(() => {
        handleApplicationAlerts({
            type:    undefined,
            content: undefined,
            reset:   true
        });
    }, [PATH_LOCATION]);

    // The data that will be soon returned by the context
    const data = {
        generalApplicationData,
        fullPageLoader,
        fullPageLoaderV2,
        setFullPageLoader,
        setFullPageLoaderV2,
        storeLocation,
        setStoreLocation,
        accessibilityFont,
        SERVER_URL,
        locations,
        setLocations,
        highContrast,
        handTalkData,
        recaptchaData,
        setRecaptchaData,
        developMode,
        socialLoginConfig,
        setSocialLoginConfig,
        applicationAlerts,
        handleAccessibilityFont,
        handleHighContrast,
        handleDocumentTitle,
        handleApplicationAlerts,
        handleDocumentTitle,
        noticesData,
        setNoticesData,
        isLocationPickerOpen,
        setIsLocationPickerOpen,
        isValidationModalOpen,
        setIsValidationModalOpen
    };

    return (
        <AppContext.Provider value={data}>
            {children}
        </AppContext.Provider>
    )
}

// Hook to provide a simple context accessibility
const useAppContext = () => useContext(AppContext);
export default useAppContext;
